import React, { useState, useEffect } from "react";
import AttendanceGrid from "./components/AttendanceGrid";
import MonthSelector from "./components/MonthSelector";
import AttendanceTotalsModal from "./components/AttendanceTotalsModal";
import { generateDaysArray, formatDateForAWS } from "../../utils/dateUtils";
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import useFetchUserByEmail from '../../components/UserInfoLoader/LoadUserInfo';
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { capitalize } from '../../components/common/capitalize';
import {
  safeListUsers,
  listAttendanceRecords,
  getDeletedUserHistory,
} from "../../graphql/queries";
import {
  createAttendanceRecord,
  updateAttendanceRecord,
} from "../../graphql/mutations";
import LoadingSpinner from "../../components/layout/LoadingSpinner";
import "./Attendance.css";

const Attendance = () => {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [days, setDays] = useState([]);
  const [changes, setChanges] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deletedUsers, setDeletedUsers] = useState({});
  const [showTotals, setShowTotals] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userEmail, setUserEmail] = useState(null);

  const { userInfo: userLoggedIn, loading: userLoading, error: userError } = useFetchUserByEmail(userEmail);

  const fetchUserDetails = async () => {
    try {
      const { signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();

      // Identify groups from the Cognito ID token
      const groups = session.tokens.idToken.payload['cognito:groups'] || [];
      setUserGroups(groups);

      // Example: parse name from signInDetails.loginId
      const [firstName, lastNameWithDomain] = signInDetails.loginId.split('.');
      const lastName = lastNameWithDomain.split('@')[0];
      setUserEmail(signInDetails.loginId)
      setUserDetails({
        firstName: capitalize(firstName),
        lastName: capitalize(lastName),
        email: signInDetails.loginId,
      });

      // Here you'd set userInfo from your GraphQL or DB reference if needed
      // For example, if userInfo is simply the email or some numeric ID:
      setUserInfo(signInDetails.loginId); // or a user ID from your DB
    } catch (error) {
      console.log('User not authenticated', error);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const columnOrder = ["P", "T", "C", "L", "I"];
  const keyMap = {
    "PTO": "P",
    "Tardy": "T",
    "Call In": "C",
    "Leave Early": "L",
    "Incentive Day": "I",
  };

  const [createRecord] = useMutation(createAttendanceRecord);
  const [updateRecord] = useMutation(updateAttendanceRecord);
  const [fetchDeletedUser] = useLazyQuery(getDeletedUserHistory);

  const {
    data: userData,
    loading: usersLoading,
    error: usersError,
  } = useQuery(safeListUsers, {
    variables: { limit: 1000 },
    fetchPolicy: "network-only",
  });

  const [fetchAttendanceRecords, { data: attendanceDataResult, loading: attendanceLoading, error: attendanceError }] =
    useLazyQuery(listAttendanceRecords, {
      fetchPolicy: "network-only",
    });

  const loadAttendanceRecords = async (token = null) => {
    try {
      const { data } = await fetchAttendanceRecords({
        variables: { limit: 1000, nextToken: token },
      });

      if (data?.listAttendanceRecords?.items) {
        setAttendanceData((prev) => [
          ...prev,
          ...data.listAttendanceRecords.items,
        ]);
      }

      if (data?.listAttendanceRecords?.nextToken) {
        setNextToken(data.listAttendanceRecords.nextToken);
      } else {
        setNextToken(null);
      }
    } catch (error) {
      console.error("Error loading paginated attendance records", error);
    }
  };

  useEffect(() => {
    setAttendanceData([]); // clear on month change or initial load
    loadAttendanceRecords(); // load first page
  }, []);

  useEffect(() => {
    const fetchAllPages = async () => {
      let token = null;
      let allData = [];

      do {
        const { data } = await fetchAttendanceRecords({
          variables: { limit: 1000, nextToken: token },
        });

        if (data?.listAttendanceRecords?.items) {
          allData = [...allData, ...data.listAttendanceRecords.items];
        }

        token = data?.listAttendanceRecords?.nextToken || null;
      } while (token);

      setAttendanceData(allData);
    };

    fetchAllPages();
  }, []);

  useEffect(() => {
    const missingUserIds = attendanceData
      .map((r) => r.userId)
      .filter(
        (userId) =>
          !teamMembers.some((member) => member.id === userId) &&
          !deletedUsers[userId]
      );

    missingUserIds.forEach((userId) => {
      fetchDeletedUser({ variables: { id: userId } }).then((res) => {
        if (res.data?.getDeletedUserHistory) {
          setDeletedUsers((prev) => ({
            ...prev,
            [userId]: res.data.getDeletedUserHistory,
          }));
        }
      });
    });
  }, [attendanceData, teamMembers, deletedUsers, fetchDeletedUser]);

  useEffect(() => {
    if (userData?.listUserFixes?.items) {
      const sorted = [...userData.listUserFixes.items].sort((a, b) => {
        if (a.team !== b.team) return a.team.localeCompare(b.team);
        const first = a.first_name.localeCompare(b.first_name);
        if (first !== 0) return first;
        return a.last_name.localeCompare(b.last_name);
      });
      setTeamMembers(sorted);
    }
  }, [userData]);

  const allUsers = [
    ...teamMembers,
    ...Object.values(deletedUsers).map((user) => ({
      ...user,
      deleted: true,
    })),
  ];

  useEffect(() => {
    if (attendanceDataResult?.listAttendanceRecords?.items) {
      setAttendanceData(attendanceDataResult.listAttendanceRecords.items);
    }
  }, [attendanceDataResult]);

  useEffect(() => {
    setDays(generateDaysArray(selectedMonth));
  }, [selectedMonth]);

  const handleAttendanceChange = (
    recordId,
    userId,
    date,
    code,
    note = ""
  ) => {
    const awsDate = formatDateForAWS(date);
    const updatedBy =
      userLoggedIn?.id || userDetails?.email || "unknown";

    setAttendanceData((prev) => {
      // ─── EDIT EXISTING (we have an id) ─────────────────────────────
      if (recordId) {
        return prev.map((r) =>
          r.id === recordId
            ? { ...r, key: code, notes: note, updatedBy }
            : r
        );
      }

      // ─── UPSERT by userId + date (no id yet) ───────────────────────
      const idx = prev.findIndex(
        (r) => r.userId === userId && r.date === awsDate
      );
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], key: code, notes: note, updatedBy };
        return updated;
      }
      return [
        ...prev,
        { userId, date: awsDate, key: code, notes: note, updatedBy },
      ];
    });

    // keep the pending‑changes array in sync
    const newChange = {
      id: recordId,            // may be undefined for brand‑new rows
      userId,
      date: awsDate,
      key: code,
      notes: note,
      updatedBy,
    };

    setChanges((prev) => [
      ...prev.filter((c) => !(c.userId === userId && c.date === awsDate)),
      newChange,
    ]);
  };


  const applyChanges = async () => {
    setIsSaving(true);
    try {
      for (const change of changes) {
        const existingRecord = attendanceData.find(
          (r) => r.userId === change.userId && r.date === change.date
        );

        const updatedBy = change.updatedBy || userLoggedIn?.id || userDetails?.email || "unknown";

        if (existingRecord?.id) {
          await updateRecord({
            variables: {
              input: {
                id: existingRecord.id,
                key: change.key,
                notes: change.notes,
                updatedBy,
              },
            },
          });
        } else {
          await createRecord({
            variables: {
              input: {
                ...change,
                updatedBy,
                createdBy: updatedBy, // 🔑 Add this only on creation
              },
            },
          });
        }
      }

      setAttendanceData([]);          // Clear current local state
      await loadAttendanceRecords();  // Load fresh data from server

      alert("Changes applied successfully!");
      setChanges([]);
    } catch (error) {
      console.error("Error applying changes:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const calculateTotals = (teamMemberId) => {
    if (!days || days.length === 0) return {};

    const displayedMonth = String(days[0].getMonth() + 1).padStart(2, '0'); // 0-based, so add 1
    const displayedYear = String(days[0].getFullYear());

    const userRecords = attendanceData.filter((r) => {
      if (r.userId !== teamMemberId) return false;

      // Get parts from YYYY-MM-DD
      const [recordYear, recordMonth] = r.date.split('-');
      return recordYear === displayedYear && recordMonth === displayedMonth;
    });

    const totals = {
      P: 0,
      T: 0,
      C: 0,
      L: 0,
      I: 0,
    };

    userRecords.forEach((record) => {
      const fullKey = record.key?.trim();
      const shortKey = keyMap[fullKey];
      if (shortKey && totals.hasOwnProperty(shortKey)) {
        totals[shortKey] += 1;
      }
    });

    return totals;
  };


  if (usersLoading || attendanceLoading || isSaving) {
    return <LoadingSpinner />;
  }
  if (usersError) return <p>Error loading users: {usersError.message}</p>;
  if (attendanceError)
    return <p>Error loading attendance data: {attendanceError.message}</p>;

  return (
    <div className="attendance-container">
      <header className="attendance-header">
        <h1 className="page-title">Team Attendance Tracker</h1>
      </header>

      <MonthSelector
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
      />
      {changes.length > 0 && (
        <button
          className="apply-changes-btn"
          onClick={applyChanges}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Apply Changes"}
        </button>
      )}

      <button className="apply-changes-btn" onClick={() => setShowTotals(true)}>
        View Totals
      </button>

      <div className="attendance-card">
        <div className="attendance-wrapper">
          <AttendanceGrid
            days={days}
            teamMembers={allUsers}
            attendanceData={attendanceData}
            onAttendanceChange={handleAttendanceChange}
            calculateTotals={calculateTotals}
          />
          <AttendanceTotalsModal
            isOpen={showTotals}
            onClose={() => setShowTotals(false)}
            teamMembers={allUsers}
            attendanceData={attendanceData}
          />
        </div>
      </div>
    </div>
  );
};

export default Attendance;
